import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Phone, EnvelopeSimple, MapPin, Clock } from "@phosphor-icons/react";
import { useSEO, breadcrumbsSchema, SITE_URL } from "@/lib/seo";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "General Inquiry", message: "", website: "" });
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Contact the Travel Desk | TheCityPair",
    description:
      "Talk to a real travel specialist about premium and business class flights from the US. Call +1 (888) 555-7873 or send us a message — we reply within 24 hours.",
    canonical: "/contact",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "ContactPage",
        "@id": `${SITE_URL}/contact`,
        url: `${SITE_URL}/contact`,
        name: "Contact the Travel Desk",
      },
      breadcrumbsSchema([
        { name: "Home", url: "/" },
        { name: "Contact", url: "/contact" },
      ]),
    ],
  });

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads", form);
      toast.success("Message received. Concierge will reply within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "General Inquiry", message: "", website: "" });
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Could not send message");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-[#F7F5F0] min-h-screen">
      <div className="bg-[#0A0A0B] relative"><Navbar dark /><div className="h-24" /></div>

      <div className="px-6 md:px-12 lg:px-24 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="contact-page">
        <div className="lg:col-span-5">
          <p className="tcp-overline text-[#C29B62] mb-4">Concierge Desk</p>
          <h1 className="font-serif text-4xl md:text-6xl tracking-tighter leading-none text-[#1C1C1C] mb-6">
            How may we be <em className="italic">of service?</em>
          </h1>
          <p className="text-[#666460] mb-10 max-w-md leading-relaxed">
            Our US-based concierge team responds within 30 seconds during business hours and within 4 hours overnight. For booking-specific support, please include your reference number.
          </p>

          <div className="space-y-5 text-sm">
            <div className="flex items-start gap-4"><Phone size={20} className="text-[#C29B62] mt-0.5" /><div><p className="font-semibold">+1 (888) 555-7873</p><p className="text-[#666460]">Toll-free across the United States</p></div></div>
            <div className="flex items-start gap-4"><EnvelopeSimple size={20} className="text-[#C29B62] mt-0.5" /><div><p className="font-semibold">concierge@thecitypair.com</p><p className="text-[#666460]">Business inquiries: partners@thecitypair.com</p></div></div>
            <div className="flex items-start gap-4"><MapPin size={20} className="text-[#C29B62] mt-0.5" /><div><p className="font-semibold">1736 Holly Oak Ln</p><p className="text-[#666460]">Chattanooga, TN 37421, USA</p></div></div>
            <div className="flex items-start gap-4"><Clock size={20} className="text-[#C29B62] mt-0.5" /><div><p className="font-semibold">Monday–Sunday, 24/7</p><p className="text-[#666460]">Emergency travel desk always staffed</p></div></div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 bg-white border border-[#E0DDD6] p-8 md:p-10 space-y-5" data-testid="contact-form">
          <p className="tcp-overline text-[#C29B62]">Send a Message</p>
          {/* Honeypot: invisible to real users; bots that fill every input get blocked. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={form.website}
            onChange={(e) => update("website", e.target.value)}
            aria-hidden="true"
            style={{ position: "absolute", left: "-10000px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><label className="tcp-label">Name</label><input required className="tcp-input" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="contact-name" /></div>
            <div><label className="tcp-label">Email</label><input type="email" required className="tcp-input" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email" /></div>
            <div><label className="tcp-label">Phone (optional)</label><input className="tcp-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="contact-phone" /></div>
            <div><label className="tcp-label">Subject</label>
              <select className="tcp-input" value={form.subject} onChange={(e) => update("subject", e.target.value)} data-testid="contact-subject">
                <option>General Inquiry</option><option>New Booking</option><option>Existing Reservation</option><option>Corporate Travel</option><option>Press / Partnerships</option>
              </select>
            </div>
          </div>
          <div><label className="tcp-label">Message</label><textarea required rows={6} className="tcp-input" value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="contact-message" /></div>
          <button type="submit" disabled={loading} className="btn-gold text-xs tracking-[0.16em] uppercase" data-testid="contact-submit">{loading ? "Sending..." : "Send Message"}</button>
        </form>
      </div>
      <Footer />
    </div>
  );
}
